import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, Check } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Shared "Workspace" design-system primitives for the Translate surface:
 * buttons, icon buttons, the segmented control, key caps, and a portal-based
 * select dropdown. Centralized here so the toolbar, panes, history panel,
 * status bar and command palette all render the identical look instead of
 * re-deriving it per file.
 */

export function WsButtonPrimary({
  children,
  kbd,
  className,
  ...rest
}: { children: ReactNode; kbd?: string; className?: string } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex items-center gap-2 h-[30px] px-3 text-[13px] font-semibold whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none disabled:transform-none transition-colors duration-150 cursor-pointer select-none active:scale-[0.98] hover:shadow-sm',
        className,
      )}
      style={{
        borderRadius: 6,
        border: '1px solid var(--accent-deep)',
        background: 'var(--accent)',
        color: 'var(--cta-fg)',
      }}
      {...rest}
    >
      {children}
      {kbd && <span className="kbd-hint font-mono text-[11px] opacity-85 px-1 py-0.5 rounded bg-black/10 dark:bg-white/10">{kbd}</span>}
    </button>
  )
}

/** `h={null}` drops the inline height so a responsive `h-*` class can own it,
 * same escape hatch as WsButtonGhost below. */
export function WsButtonSecondary({
  children,
  icon,
  className,
  h = 30,
  ...rest
}: { children: ReactNode; icon?: ReactNode; className?: string; h?: number | null } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex items-center gap-[7px] px-2.5 text-[13px] font-medium whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none transition-colors duration-150 hover:bg-[var(--card-2)] hover:border-[var(--line-strong,var(--line))] hover:shadow-xs active:scale-[0.98] cursor-pointer select-none',
        className,
      )}
      style={{ ...(h == null ? null : { height: h }), borderRadius: 6, border: '1px solid var(--line)', background: 'var(--card)', color: 'var(--ink)' }}
      {...rest}
    >
      {icon}
      {children}
    </button>
  )
}

/** `h={null}` drops the inline height so a responsive `h-*` class can own it —
 * inline styles always beat utility classes, which is why the prop exists. */
export function WsButtonGhost({
  children,
  icon,
  className,
  h = 28,
  ...rest
}: { children: ReactNode; icon?: ReactNode; className?: string; h?: number | null } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex items-center gap-1.5 px-2 text-[12.5px] font-medium disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none transition-colors duration-150 hover:bg-[var(--card-2)] hover:text-[var(--ink)] cursor-pointer select-none',
        className,
      )}
      style={{ ...(h == null ? null : { height: h }), borderRadius: 6, border: 0, background: 'transparent', color: 'var(--muted)' }}
      {...rest}
    >
      {icon}
      {children}
    </button>
  )
}

export function WsIconButton({
  icon,
  label,
  active,
  size = 28,
  className,
  ...rest
}: {
  icon: ReactNode
  label: string
  active?: boolean
  size?: number
  className?: string
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      data-active={active ? 'true' : undefined}
      className={cn(
        'inline-flex items-center justify-center transition-colors duration-150 cursor-pointer select-none disabled:opacity-40 disabled:cursor-not-allowed',
        active
          ? 'bg-[var(--accent-soft)] text-[var(--accent-deep)] shadow-xs'
          : 'bg-transparent text-[var(--muted)] hover:bg-[var(--card-2)] hover:text-[var(--ink)]',
        className,
      )}
      style={{
        width: size,
        height: size,
        borderRadius: 6,
        border: 0,
      }}
      {...rest}
    >
      {icon}
    </button>
  )
}

/** Mono keycap. Carries `kbd-hint`, which globals.css hides on touch devices —
 * a ⌘K badge is noise on a phone. */
export function WsKbd({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={cn('kbd-hint font-mono inline-flex items-center h-[18px] px-[5px] text-[10.5px] shrink-0 font-medium select-none', className)}
      style={{ borderRadius: 4, border: '1px solid var(--line)', background: 'var(--card-2)', color: 'var(--muted)' }}
    >
      {children}
    </span>
  )
}

export function WsSegmented<T extends string>({
  items,
  value,
  onChange,
  h = 30,
  ariaLabel,
}: {
  items: { id: T; label: ReactNode; disabled?: boolean }[]
  value: T
  onChange: (v: T) => void
  h?: number
  ariaLabel?: string
}) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className="inline-flex shrink-0 p-0.5"
      style={{ borderRadius: 6, background: 'var(--card-2)', border: '1px solid var(--line-hair)' }}
    >
      {items.map((item) => {
        const active = item.id === value
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={item.disabled}
            onClick={() => onChange(item.id)}
            className={cn(
              'touch-44-y inline-flex items-center justify-center px-3 md:px-2.5 text-[13px] md:text-[12.5px] font-medium whitespace-nowrap transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed select-none cursor-pointer',
              active
                ? 'font-semibold shadow-xs'
                : 'hover:text-[var(--ink)] hover:bg-[var(--card)]/50',
            )}
            style={{
              height: h - 6,
              borderRadius: 4,
              color: active ? 'var(--ink)' : 'var(--muted-2)',
              background: active ? 'var(--card)' : 'transparent',
              border: 0,
            }}
          >
            {item.label}
          </button>
        )
      })}
    </div>
  )
}

export interface WsSelectOption {
  id: string
  label: string
  sub?: string
  disabled?: boolean
}

/**
 * Portal-based select trigger + dropdown — the toolbar's `[English ▾]`,
 * and the restyled language picker. Portals to
 * `document.body` so the menu is never clipped by the toolbar's overflow
 * context.
 */
export function WsSelect({
  value,
  options,
  onChange,
  label,
  width,
  align = 'start',
  ariaLabel,
  className,
}: {
  value: string
  options: WsSelectOption[]
  onChange: (id: string) => void
  label?: string
  width?: number | string
  align?: 'start' | 'end'
  ariaLabel?: string
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const [pos, setPos] = useState({ top: 0, left: 0, width: 220 })
  const current = options.find((o) => o.id === value)

  useEffect(() => setMounted(true), [])

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    const menuWidth = Math.max(rect.width, 200)
    let left = align === 'end' ? rect.right - menuWidth : rect.left
    left = Math.max(8, Math.min(left, window.innerWidth - menuWidth - 8))
    setPos({ top: rect.bottom + 4, left, width: menuWidth })
  }, [open, align])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node
      if (triggerRef.current?.contains(t)) return
      if (menuRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    const onScroll = () => setOpen(false)
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
    }
  }, [open])

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'touch-44-y inline-flex items-center gap-1.5 h-[30px] min-w-0 pl-2.5 pr-2 text-[13px] whitespace-nowrap transition-colors duration-150 hover:bg-[var(--card-2)] hover:border-[var(--line-strong,var(--line))] cursor-pointer select-none',
          className,
        )}
        style={{ width, borderRadius: 6, border: '1px solid var(--line)', background: 'var(--card)', color: 'var(--ink)' }}
      >
        {label && <span className="shrink-0" style={{ color: 'var(--muted-2)', fontSize: 12 }}>{label}</span>}
        <span className="flex-1 min-w-0 text-left truncate" style={{ fontWeight: 500 }}>{current?.label ?? value}</span>
        <ChevronDown size={13} className={cn('shrink-0 transition-transform duration-200', open && 'rotate-180')} style={{ color: 'var(--muted-2)' }} aria-hidden />
      </button>
      {mounted && open &&
        createPortal(
          <div
            ref={menuRef}
            role="listbox"
            aria-label={ariaLabel}
            className="fixed z-[10000] dvh-max-70 overflow-y-auto p-1 animate-scale-in"
            style={{
              top: pos.top,
              left: pos.left,
              width: pos.width,
              borderRadius: 6,
              border: '1px solid var(--line)',
              background: 'var(--card)',
              boxShadow: 'var(--shadow-lg)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
            }}
          >
            {options.map((opt) => {
              const selected = opt.id === value
              return (
                <button
                  key={opt.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  disabled={opt.disabled}
                  onClick={() => { if (opt.disabled) return; onChange(opt.id); setOpen(false) }}
                  className="w-full flex items-center gap-2.5 text-left px-2 min-h-[44px] md:min-h-0 py-[7px] text-[13px] transition-colors duration-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer rounded"
                  style={{
                    background: selected ? 'var(--accent-soft)' : 'transparent',
                    color: selected ? 'var(--accent-ink)' : 'var(--ink)',
                    fontWeight: selected ? 600 : 400,
                  }}
                  onMouseEnter={(e) => { if (!selected && !opt.disabled) e.currentTarget.style.background = 'var(--card-2)' }}
                  onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = 'transparent' }}
                >
                  <span className="flex-1 min-w-0 truncate">{opt.label}</span>
                  {opt.sub && <span className="shrink-0 text-[12px]" style={{ color: 'var(--muted-2)' }}>{opt.sub}</span>}
                  {selected && <Check size={13} className="shrink-0" style={{ color: 'var(--accent-deep)' }} aria-hidden />}
                </button>
              )
            })}
          </div>,
          document.body,
        )}
    </>
  )
}

/** A small pill toggle switch — the one place the design allows a full pill. */
export function WsToggle({
  checked,
  onChange,
  label,
  badge,
  disabled,
}: {
  checked: boolean
  onChange: () => void
  label: string
  badge?: string
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      data-keep-pill="true"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      disabled={disabled}
      className="touch-44-y inline-flex items-center gap-[7px] h-[30px] px-2.5 text-[12.5px] transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer select-none"
      style={{
        borderRadius: 6,
        border: `1px solid ${checked ? 'var(--accent)' : 'var(--line)'}`,
        background: checked ? 'var(--accent-soft)' : 'var(--card)',
        color: checked ? 'var(--accent-ink)' : 'var(--ink)',
      }}
    >
      <span
        data-keep-pill="true"
        className="relative inline-block shrink-0 transition-colors duration-200"
        style={{ width: 26, height: 15, borderRadius: 999, background: checked ? 'var(--accent)' : 'var(--line)' }}
      >
        <span
          data-keep-pill="true"
          className="absolute transition-transform duration-200"
          style={{ top: 2, left: checked ? 13 : 2, width: 11, height: 11, borderRadius: 999, background: checked ? 'var(--cta-fg)' : 'var(--card)' }}
        />
      </span>
      {label}
      {badge && <span className="font-bold text-[10.5px]" style={{ color: 'var(--accent-deep)' }}>{badge}</span>}
    </button>
  )
}

/** Convenience hook: outside-click + Escape close for simple non-portal popovers. */
export function useOutsideClose(open: boolean, onClose: () => void, refs: React.RefObject<HTMLElement | null>[]) {
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  const handler = useCallback((e: MouseEvent | KeyboardEvent) => {
    if (e instanceof KeyboardEvent) {
      if (e.key === 'Escape') closeRef.current()
      return
    }
    const t = e.target as Node
    if (refs.some((r) => r.current?.contains(t))) return
    closeRef.current()
  }, [refs])
  useEffect(() => {
    if (!open) return
    document.addEventListener('mousedown', handler)
    document.addEventListener('keydown', handler)
    return () => {
      document.removeEventListener('mousedown', handler)
      document.removeEventListener('keydown', handler)
    }
  }, [open, handler])
}
